import type { ResourceScope } from '@kop/contracts';
import {importedWorkRegisterSchema,type ImportedWorkRegister} from '@kop/work-control';
import { onSensitiveMemoryCacheClear } from '../../runtime/sensitive-cache-lifecycle';
import { assertWorkLedgerDetailScope, assertWorkLedgerResponseScope } from './work-ledger-session';
import {
  workAggregateAllowedActionV3Schema,
  workAggregateV3Schema,
  type WorkAggregateV3,
} from '@kop/work-control';
import {
  createWorkControlV3EmulatorPort,
  createWorkControlV3FirebasePort,
  type WorkControlV3Scope,
  type WorkControlV3MutationReceipt,
} from '@kop/operations-firebase-adapter';
export type WorkLedgerNextGate =
  | 'permit-draft'
  | 'correction'
  | 'equipment-plans'
  | 'approval'
  | 'issue'
  | 'start'
  | 'tbm'
  | 'field-acknowledgement'
  | 'publish'
  | 'patrol'
  | 'gas-remeasurement'
  | 'closeout'
  | 'none';

export type WorkLedgerRow = {
  workId: string;
  version: number;
  title: string;
  contractorName: string;
  area: string;
  startsAt: string;
  endsAt: string;
  source: 'workspace' | 'external-intake' | 'register-import';
  imported?: ImportedWorkRegister;
  lifecycle: WorkAggregateV3['lifecycle'];
  nextGate: WorkLedgerNextGate;
  statusSummary: readonly string[];
  evidenceComplete: number;
  evidenceRequired: number;
  allowedActions: WorkAggregateV3['allowedActions'];
  updatedAt: string;
  register?: {
    days?: Record<string, {approved: boolean; sourceHash: string; attendees: number; signed: number; closedAt: string | null}>;
    authorName?: string | null;
    dailyEvidence?: {
      startedAt: string | null;
      completedAt: string | null;
      issuedAt: string | null;
      patrols: readonly { at: string; actionRequired: boolean; resolvedAt: string | null }[];
    };
    workTypes: WorkAggregateV3['permitDetails']['workTypes'];
    hazardLabels: readonly string[];
    safetyContacts: readonly string[];
    plannedHeadcount: number | null;
    remarks: string | null;
    riskAssessment: 'permit-checklist' | 'not-recorded';
    workPlan: 'not-applicable' | 'reviewed' | 'review-required';
    workPlanReason: string | null;
    patrolCount: number;
    closeoutRecorded: boolean;
  };
};

export type ExactWorkLedgerSummary = {
  total: number;
  needsAction: number;
  active: number;
  patrolOverdue: number;
  complete: boolean;
  generatedAt: string;
  receiptId: string;
};

export type WorkLedgerProjection = {
  rows: readonly WorkLedgerRow[];
  summary: ExactWorkLedgerSummary | null;
  connected: boolean;
  errorCode: string | null;
  exhausted?: boolean;
  heldCount?: number;
  nextCursor?: WorkLedgerCursor | null;
};

export type WorkLedgerCursor = Readonly<{ updatedAt: string; workId: string; documentId?: string; phase?: 'register' }>;

export type WorkLedgerSaveInput = Readonly<{
  aggregate: WorkAggregateV3;
  expectedVersion: number;
  idempotencyKey: string;
  changeReason?: string;
  authoringContextId?: string;
  submitForApproval?: true;
}>;

export type WorkAuthoringContext = Readonly<{
  contextId: string;
  workId: string;
  scope: WorkControlV3Scope;
  contractorBindingId: string | null;
  refs: Readonly<Record<string, unknown>>;
  defaults: Readonly<Record<string, unknown>>;
  expiresAt: string;
}>;

export interface WorkLedgerPort {
  manageDay?(input: Readonly<Record<string, unknown>>): Promise<Readonly<Record<string, unknown>>>;
  reserveAuthoringContext(input: {
    scope: ResourceScope;
    idempotencyKey: string;
  }): Promise<WorkAuthoringContext>;
  list(scope: ResourceScope, cursor?: WorkLedgerCursor | null): Promise<WorkLedgerProjection>;
  detail(scope: ResourceScope, workId: string): Promise<WorkAggregateV3>;
  save(input: WorkLedgerSaveInput): Promise<WorkControlV3MutationReceipt>;
  submitForApproval(input: Readonly<Record<string, unknown>>): Promise<WorkControlV3MutationReceipt>;
  issuePermit(input: Readonly<Record<string, unknown>>): Promise<WorkControlV3MutationReceipt>;
  startWork(input: Readonly<Record<string, unknown>>): Promise<WorkControlV3MutationReceipt>;
  recordPatrol(input: Readonly<Record<string, unknown>>): Promise<WorkControlV3MutationReceipt>;
  resolvePatrolCorrection(input: Readonly<Record<string, unknown>>): Promise<WorkControlV3MutationReceipt>;
  verifyChecklistCorrection(input: Readonly<Record<string, unknown>>): Promise<WorkControlV3MutationReceipt>;
  confirmCloseout(input: Readonly<Record<string, unknown>>): Promise<WorkControlV3MutationReceipt>;
  reviewEquipmentPlan(
    input: Readonly<Record<string, unknown>>,
  ): Promise<WorkControlV3MutationReceipt>;
  preparePublication(
    input: Readonly<Record<string, unknown>>,
  ): Promise<Readonly<Record<string, unknown>>>;
  publish(input: Readonly<Record<string, unknown>>): Promise<WorkControlV3MutationReceipt>;
  loadMyPendingSignatures(
    input?: Readonly<Record<string, unknown>>,
  ): Promise<Readonly<Record<string, unknown>>>;
  loadMyInfo(scope: ResourceScope): Promise<Readonly<Record<string, unknown>>>;
  bindPersonalSignatureUse(
    scope: ResourceScope,
    binding: Readonly<Record<string, unknown>>,
  ): Promise<Readonly<Record<string, unknown>>>;
  hardDelete(input: {
    workId: string;
    expectedVersion: number;
    idempotencyKey: string;
  }): Promise<WorkControlV3MutationReceipt>;
  renderBundle(input: {
    workId: string;
    expectedVersion: number;
    idempotencyKey: string;
  }): Promise<Readonly<Record<string, unknown>>>;
}

class DisabledWorkLedgerPort implements WorkLedgerPort {
  async reserveAuthoringContext(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async list(scope: ResourceScope): Promise<WorkLedgerProjection> {
    assertWorkControlScope(scope);
    return {
      rows: [],
      summary: null,
      connected: false,
      errorCode: 'WORK_CONTROL_V3_ADAPTER_UNAVAILABLE',
    };
  }

  async detail(scope: ResourceScope): Promise<never> {
    assertWorkControlScope(scope);
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async save(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async submitForApproval(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async issuePermit(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async startWork(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async recordPatrol(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async resolvePatrolCorrection(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async verifyChecklistCorrection(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async confirmCloseout(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async reviewEquipmentPlan(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async preparePublication(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async publish(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async loadMyPendingSignatures(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async loadMyInfo(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async bindPersonalSignatureUse(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async hardDelete(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
  async renderBundle(): Promise<never> {
    throw new Error('WORK_CONTROL_V3_ADAPTER_UNAVAILABLE');
  }
}

type Environment = Readonly<Record<string, string | boolean | undefined>>;
type Loopback = '127.0.0.1' | 'localhost' | '::1';

function endpoint(value: string | boolean | undefined, fallback: number) {
  const [host, port] = String(value || `127.0.0.1:${fallback}`)
    .replace(/^https?:\/\//u, '')
    .split(':');
  if (!['127.0.0.1', 'localhost', '::1'].includes(host ?? '') || !Number(port))
    throw new Error('WORK_CONTROL_V3_LOOPBACK_ONLY');
  return { host: host as Loopback, port: Number(port) };
}

function object(value: unknown): Readonly<Record<string, unknown>> {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('WORK_CONTROL_V3_PROJECTION_INVALID');
  return value as Readonly<Record<string, unknown>>;
}

function transportScope(scope: ResourceScope): WorkControlV3Scope {
  assertWorkControlScope(scope);
  if (scope.kind === 'tenant') throw new Error('WORK_CONTROL_EXACT_EUMSEONG_SCOPE_REQUIRED');
  return scope.kind === 'contractor'
    ? {
        kind: 'contractor',
        tenantId: 'kop',
        siteId: scope.siteId === 'eumseong' ? 'site-eumseong' : scope.siteId,
        contractorId: scope.contractorId,
      }
    : {
        kind: 'site',
        tenantId: 'kop',
        siteId: scope.siteId === 'eumseong' ? 'site-eumseong' : scope.siteId,
      };
}

function ledgerRow(value: unknown): WorkLedgerRow {
  const row = object(value);
  const lifecycles = new Set<WorkAggregateV3['lifecycle']>([
    'draft',
    'returned',
    'approval-pending',
    'approved',
    'issued',
    'tbm-complete',
    'ready-for-tbm',
    'tbm-stale',
    'ready-to-publish',
    'active',
    'suspended',
    'completion-requested',
    'closed',
    'invalidated',
  ]);
  const nextGates = new Set<WorkLedgerNextGate>([
    'permit-draft',
    'correction',
    'equipment-plans',
    'approval',
    'issue',
    'start',
    'tbm',
    'field-acknowledgement',
    'publish',
    'patrol',
    'gas-remeasurement',
    'closeout',
    'none',
  ]);
  if (
    typeof row.workId !== 'string' ||
    !Number.isSafeInteger(row.version) ||
    Number(row.version) < 1 ||
    typeof row.title !== 'string' ||
    typeof row.contractorName !== 'string' ||
    typeof row.area !== 'string' ||
    typeof row.startsAt !== 'string' ||
    typeof row.endsAt !== 'string' ||
    !Number.isFinite(Date.parse(row.startsAt)) ||
    !Number.isFinite(Date.parse(row.endsAt)) ||
    !['workspace', 'external-intake', 'register-import'].includes(String(row.source)) ||
    !lifecycles.has(row.lifecycle as WorkAggregateV3['lifecycle']) ||
    !nextGates.has(row.nextGate as WorkLedgerNextGate) ||
    !Array.isArray(row.statusSummary) ||
    row.statusSummary.some((status) => typeof status !== 'string') ||
    !Number.isSafeInteger(row.evidenceComplete) ||
    !Number.isSafeInteger(row.evidenceRequired) ||
    Number(row.evidenceComplete) < 0 ||
    Number(row.evidenceRequired) < Number(row.evidenceComplete) ||
    !Array.isArray(row.allowedActions) ||
    row.allowedActions.some(
      (action) => !workAggregateAllowedActionV3Schema.safeParse(action).success,
    ) ||
    typeof row.updatedAt !== 'string' ||
    !Number.isFinite(Date.parse(row.updatedAt))
  )
    throw new Error('WORK_CONTROL_V3_PROJECTION_INVALID');
  if (row.source === 'register-import') {
    const imported = importedWorkRegisterSchema.parse(row.imported);
    if (imported.workId !== row.workId || row.allowedActions.length || row.nextGate !== 'none')
      throw new Error('WORK_CONTROL_V3_IMPORTED_PROJECTION_INVALID');
  }
  return row as WorkLedgerRow;
}

function exactSummary(value: unknown): ExactWorkLedgerSummary {
  const summary = object(value);
  if (
    !['total', 'needsAction', 'active', 'patrolOverdue'].every(
      (key) => Number.isSafeInteger(summary[key]) && Number(summary[key]) >= 0,
    ) ||
    typeof summary.generatedAt !== 'string' ||
    typeof summary.receiptId !== 'string' ||
    typeof summary.complete !== 'boolean'
  )
    throw new Error('WORK_CONTROL_V3_SUMMARY_INVALID');
  return summary as ExactWorkLedgerSummary;
}

export function parseWorkLedgerProjection(input: unknown): WorkLedgerProjection {
  const value = object(input);
  if (!Array.isArray(value.rows) || value.summary == null ||
    (value.heldCount != null && (!Number.isSafeInteger(value.heldCount) || Number(value.heldCount) < 0)))
    throw new Error('WORK_CONTROL_V3_PROJECTION_INVALID');
  let heldCount = Number(value.heldCount ?? 0);
  const rows = value.rows.flatMap(row => {
    try { return [ledgerRow(row)]; } catch { heldCount += 1; return []; }
  });
  const summary = exactSummary(value.summary);
  const cursor = value.nextCursor == null ? null : object(value.nextCursor);
  return {
    rows, heldCount,
    summary: { ...summary, complete: summary.complete && heldCount === 0 },
    connected: value.connected === true,
    errorCode: value.errorCode == null ? null : String(value.errorCode),
    exhausted: value.exhausted === true,
    nextCursor: cursor == null ? null : {
      updatedAt: String(cursor.updatedAt ?? ''), workId: String(cursor.workId ?? ''),
      ...(typeof cursor.documentId === 'string' ? { documentId: cursor.documentId } : {}),
      ...(cursor.phase === 'register' ? { phase: 'register' as const } : {}),
    },
  };
}

class EmulatorWorkLedgerPort implements WorkLedgerPort {
  constructor(private readonly adapter: ReturnType<typeof createWorkControlV3EmulatorPort>) {}

  async reserveAuthoringContext(input: {
    scope: ResourceScope;
    idempotencyKey: string;
  }) {
    const reserve = this.adapter.reserveAuthoringContext as (input: {
      scope: WorkControlV3Scope;
      idempotencyKey: string;
    }) => Promise<Readonly<Record<string, unknown>>>;
    const value = object(
      await reserve({
        scope: transportScope(input.scope),
        idempotencyKey: input.idempotencyKey,
      }),
    );
    if (
      typeof value.contextId !== 'string' ||
      typeof value.workId !== 'string' ||
      typeof value.expiresAt !== 'string' ||
      !value.scope ||
      !value.refs ||
      !value.defaults
    )
      throw new Error('WORK_CONTROL_V3_AUTHORING_CONTEXT_INVALID');
    assertWorkLedgerResponseScope(input.scope, value.scope);
    return value as WorkAuthoringContext;
  }

  async list(scope: ResourceScope, cursor: WorkLedgerCursor | null = null): Promise<WorkLedgerProjection> {
    assertWorkControlScope(scope);
    return parseWorkLedgerProjection(await this.adapter.list({ scope: transportScope(scope), pageSize: 30, cursor }));
  }

  async detail(scope: ResourceScope, workId: string): Promise<WorkAggregateV3> {
    assertWorkControlScope(scope);
    const aggregate = workAggregateV3Schema.parse(await this.adapter.detail(transportScope(scope), workId));
    assertWorkLedgerDetailScope(scope, aggregate.scope);
    if (aggregate.workId !== workId) throw new Error('WORK_CONTROL_RESPONSE_ID_MISMATCH');
    return aggregate;
  }

  save(input: WorkLedgerSaveInput) {
    return this.adapter.save(input);
  }

  manageDay(input: Readonly<Record<string, unknown>>) {
    return this.adapter.manageDay(input);
  }

  submitForApproval(input: Readonly<Record<string, unknown>>) {
    return this.adapter.submitForApproval(input);
  }

  issuePermit(input: Readonly<Record<string, unknown>>) {
    return this.adapter.issuePermit(input);
  }

  startWork(input: Readonly<Record<string, unknown>>) {
    return this.adapter.startWork(input);
  }

  recordPatrol(input: Readonly<Record<string, unknown>>) {
    return this.adapter.recordPatrol(input);
  }

  resolvePatrolCorrection(input: Readonly<Record<string, unknown>>) {
    return this.adapter.resolvePatrolCorrection(input);
  }
  verifyChecklistCorrection(input: Readonly<Record<string, unknown>>) {
    return this.adapter.verifyChecklistCorrection(input);
  }

  confirmCloseout(input: Readonly<Record<string, unknown>>) {
    return this.adapter.confirmCloseout(input);
  }

  reviewEquipmentPlan(input: Readonly<Record<string, unknown>>) {
    return this.adapter.reviewEquipmentPlan(input);
  }

  preparePublication(input: Readonly<Record<string, unknown>>) {
    return this.adapter.preparePublication(input);
  }

  publish(input: Readonly<Record<string, unknown>>) {
    return this.adapter.publish(input);
  }

  loadMyPendingSignatures(input?: Readonly<Record<string, unknown>>) {
    return this.adapter.loadMyPendingSignatures(input);
  }

  loadMyInfo(scope: ResourceScope) {
    return this.adapter.loadMyInfo(transportScope(scope));
  }

  bindPersonalSignatureUse(scope: ResourceScope, binding: Readonly<Record<string, unknown>>) {
    return this.adapter.bindPersonalSignatureUse(transportScope(scope), binding);
  }

  hardDelete(input: { workId: string; expectedVersion: number; idempotencyKey: string }) {
    return this.adapter.hardDelete(input);
  }

  renderBundle(input: { workId: string; expectedVersion: number; idempotencyKey: string }) {
    return this.adapter.renderBundle(input);
  }
}

export function assertWorkControlScope(scope: ResourceScope): void {
  if (
    scope.tenantId !== 'kop' ||
    scope.kind === 'tenant' ||
    !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(scope.siteId) ||
    (scope.kind === 'contractor' && !scope.contractorId)
  ) {
    throw new Error('WORK_CONTROL_CONFIGURED_SITE_SCOPE_REQUIRED');
  }
}

function createDefaultPort(environment: Environment = import.meta.env): WorkLedgerPort {
  const mode =
    environment.VITE_WORK_CONTROL_V3_ADAPTER ??
    (environment.VITE_AUTH_ADAPTER === 'firebase' ? 'firebase' : undefined);
  if (mode === 'firebase') {
    const config = {
      projectId: String(environment.VITE_FIREBASE_PROJECT_ID ?? ''),
      apiKey: String(environment.VITE_FIREBASE_API_KEY ?? ''),
      appId: String(environment.VITE_FIREBASE_APP_ID ?? ''),
      authDomain: String(environment.VITE_FIREBASE_AUTH_DOMAIN ?? ''),
      appCheckSiteKey: String(environment.VITE_FIREBASE_APPCHECK_SITE_KEY ?? ''),
    };
    if (config.projectId !== 'kop-safety-51efb')
      throw new Error('WORK_CONTROL_V3_CORPORATE_PROJECT_REQUIRED');
    if (Object.values(config).some((value) => !value.trim()))
      throw new Error('WORK_CONTROL_V3_FIREBASE_CONFIG_REQUIRED');
    const adapter = createWorkControlV3FirebasePort(config);
    return new EmulatorWorkLedgerPort(adapter);
  }
  if (mode !== 'firebase-emulator') return new DisabledWorkLedgerPort();
  const projectId = String(environment.VITE_FIREBASE_PROJECT_ID ?? '');
  if (!projectId.startsWith('demo-')) throw new Error('WORK_CONTROL_V3_DEMO_PROJECT_REQUIRED');
  const adapter = createWorkControlV3EmulatorPort({
    projectId,
    firestore: endpoint(environment.VITE_FIRESTORE_EMULATOR_HOST, 18080),
    functions: endpoint(environment.VITE_FUNCTIONS_EMULATOR_HOST, 15001),
  });
  return new EmulatorWorkLedgerPort(adapter);
}

let holder: WorkLedgerPort | null = null;
onSensitiveMemoryCacheClear(() => { holder = null; });

export function getWorkLedgerPort(): WorkLedgerPort {
  holder ??= createDefaultPort();
  return holder;
}

export function setWorkLedgerPortForTest(port: WorkLedgerPort | null): void {
  holder = port;
}
